import { useMemo, useCallback } from 'react'
import { useRouter } from 'next/router'
import useSWR from 'swr'
import useSWRInfinite from 'swr/infinite'
import { SearchParams } from 'typesense/lib/Typesense/Documents'

import { TypesenseSupplier } from '../utils/types/search'
import { Supplier } from '../utils/types/Product'
import { getCountryAndLocaleStrings } from '../utils/locales'
import { typesenseInstantSearchAdapter } from '../utils/typesense/config'
import { ActiveCountryId, ActiveLocaleId } from '../utils/constants'

// Extended Supplier interface for Typesense data that includes productsCount
export interface SupplierWithProductsCount extends Supplier {
  productsCount?: number
}

interface TypesenseSuppliers {
  data: Supplier[]
  isLoading: boolean
  total: number
  error: any
}

interface TypesenseSuppliersConfig {
  limit?: number
  sortBy?: string
  filterBy?: string
  countries?: ActiveCountryId[]
  locales?: ActiveLocaleId[]
  searchTerm?: string
  supplierCodes?: string[]
  supplierNames?: string[]
  page?: number
}

// Helper function to get supplier-specific include fields
const getSupplierIncludeFields = (
  locales: ActiveLocaleId[] = ['en', 'fi', 'sv', 'de'],
): string => [
  'id',
  'name',
  'code',
  'image',
  'vibeIcon',
  'pos',
  'productsCount',
  'delivery-details',
  'delivery-costs',
  ...locales.map((locale) => [
    `description_${locale}`,
  ]),
].flat().join(',')

const { typesenseClient } = typesenseInstantSearchAdapter

const useTypesenseSuppliers = (config?: TypesenseSuppliersConfig): TypesenseSuppliers => {
  const {
    limit = 24,
    sortBy = '',
    filterBy,
    countries = ['fi', 'sv', 'de', 'at', 'be', 'nl', 'it'],
    locales = ['en', 'fi', 'sv', 'de'],
    searchTerm,
    supplierCodes,
    supplierNames,
    page = 1,
  } = config || {}

  const { locale: countryAndLocaleString } = useRouter()
  const { country } = getCountryAndLocaleStrings(countryAndLocaleString)

  const buildFilterBy = () => {
    const filters: string[] = []

    if (filterBy) {
      filters.push(filterBy)
    }

    if (supplierCodes && supplierCodes.length > 0) {
      filters.push(`code:=[${supplierCodes.map((code) => `"${code}"`).join(',')}]`)
    }

    if (supplierNames && supplierNames.length > 0) {
      filters.push(`name:=[${supplierNames.map((name) => `"${name}"`).join(',')}]`)
    }

    if (countries.length > 0) {
      const countryFilters = countries.map((filterCountry) => `country-availability:=[${filterCountry.toUpperCase()}]`)
      filters.push(`(${countryFilters.join(' || ')})`)
    }

    return filters.length > 0 ? filters.join(' && ') : undefined
  }

  const { data: results, error } = useSWR(
    ['suppliers', limit, sortBy, filterBy, countries, locales, searchTerm, supplierCodes, supplierNames, country, page],
    async () => {
      try {
        const searchParams: SearchParams = {
          q: searchTerm || '*',
          query_by: searchTerm ? 'name,code,description_en,description_fi,description_sv,description_de' : undefined,
          ...(sortBy && { sort_by: sortBy }),
          per_page: limit,
          page,
          filter_by: buildFilterBy(),
          include_fields: getSupplierIncludeFields(locales),
          enable_lazy_filter: true,
        }

        const response = await typesenseClient.collections<TypesenseSupplier>('suppliers')
          .documents().search(searchParams, { cacheSearchResultsForSeconds: 600 })

        return response
      } catch (err) {
        console.error('Error fetching suppliers from Typesense:', err)
        return null
      }
    },
    {
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
      dedupingInterval: 60000, // 1 minute deduping
    },
  )

  const isLoading = !results && !error

  const suppliers = useMemo(() => {
    if (!results?.hits) return []

    return results.hits
      .map((hit) => {
        const supplier = hit.document as TypesenseSupplier

        // Transform TypesenseSupplier to Supplier interface
        return {
          id: supplier.id,
          'supplier.id': supplier.id,
          'supplier.code': supplier.code,
          'supplier.label': supplier.name,
          'supplier.status': 1,
          'supplier.ctime': '',
          'supplier.address': [],
          'supplier/service': [],
          'supplier/text': [],
          'supplier/media': [],
          productsCount: supplier.productsCount,
          // For compatibility with getSupplierDeliveryDetails
          'service.config': supplier['delivery-details'] || {},
          'service.price': supplier['delivery-costs'] || {},
          media: [
            {
              id: '',
              'media.previews': { 1280: supplier.image },
              'media.type': 'default',
              'media.languageid': null,
              'media.mimetype': '',
              'media.domain': 'supplier',
              'media.label': supplier.name,
              'media.preview': supplier.image,
              'media.url': supplier.image,
            },
            // Add vibe icon if available
            ...(supplier.vibeIcon ? [{
              id: '',
              'media.previews': { 1280: supplier.vibeIcon },
              'media.type': 'supplier-image',
              'media.languageid': null,
              'media.mimetype': '',
              'media.domain': 'supplier',
              'media.label': `${supplier.name} vibe icon`,
              'media.preview': supplier.vibeIcon,
              'media.url': supplier.vibeIcon,
            }] : []),
          ],
          text: locales
            .filter((locale) => supplier[`description_${locale}`])
            .map((locale) => ({
              'text.id': '',
              id: '',
              'text.label': 'description',
              'text.type': 'long',
              'text.content': supplier[`description_${locale}`] || '',
              'text.languageid': locale,
              'text.domain': 'supplier',
            })),
          product: [],

        } as SupplierWithProductsCount
      })
  }, [results?.hits, country, countries, locales])

  return {
    data: suppliers,
    isLoading,
    total: results?.found || 0,
    error,
  }
}

// Hook for infinite loading with pagination using useSWRInfinite
export const useTypesenseSuppliersInfinite = (config?: Omit<TypesenseSuppliersConfig, 'page'>) => {
  const {
    limit = 24,
    sortBy = '',
    filterBy,
    countries = ['fi', 'sv', 'de', 'at', 'be', 'nl', 'it'],
    locales = ['en', 'fi', 'sv', 'de'],
    searchTerm,
    supplierCodes,
    supplierNames,
  } = config || {}

  const { locale: countryAndLocaleString } = useRouter()
  const { country } = getCountryAndLocaleStrings(countryAndLocaleString)

  const buildFilterBy = useCallback(() => {
    const filters: string[] = []

    if (countries.length > 0) {
      const countryFilters = countries.map((filterCountry) => `country-availability:=[${filterCountry.toUpperCase()}]`)
      filters.push(`(${countryFilters.join(' || ')})`)
    }

    if (supplierCodes && supplierCodes.length > 0) {
      filters.push(`code:=${supplierCodes.join(',')}`)
    }

    if (supplierNames && supplierNames.length > 0) {
      filters.push(`name:=${supplierNames.join(',')}`)
    }

    if (filterBy) {
      filters.push(filterBy)
    }

    return filters.length > 0 ? filters.join(' && ') : undefined
  }, [countries, supplierCodes, supplierNames, filterBy])

  const getKey = useCallback((pageIndex: number, previousPageData: any) => {
    // If we've reached the end, return null to stop fetching
    if (previousPageData && (!previousPageData.hits || previousPageData.hits.length === 0)) {
      return null
    }

    return `suppliers-infinite-${pageIndex + 1}-${limit}-${sortBy}-${country}`
  }, [limit, sortBy, country])

  const fetcher = useCallback(async (key: string) => {
    // Extract page number from the key
    const pageMatch = key.match(/suppliers-infinite-(\d+)-/)
    const page = pageMatch ? parseInt(pageMatch[1], 10) : 1

    try {
      const searchParams: SearchParams = {
        q: searchTerm || '*',
        query_by: searchTerm ? 'name,code,description_en,description_fi,description_sv,description_de' : undefined,
        ...(sortBy && { sort_by: sortBy }),
        per_page: limit,
        page,
        filter_by: buildFilterBy(),
        include_fields: getSupplierIncludeFields(locales),
        enable_lazy_filter: true,
      }

      const response = await typesenseClient.collections<TypesenseSupplier>('suppliers')
        .documents().search(searchParams, { cacheSearchResultsForSeconds: 600 })

      return response
    } catch (err) {
      console.error('Typesense suppliers search error:', err)
      throw err
    }
  }, [searchTerm, sortBy, limit, buildFilterBy, locales, typesenseClient])

  const { data, error, size, setSize, isValidating } = useSWRInfinite(getKey, fetcher, {
    revalidateOnFocus: false,
    revalidateOnReconnect: false,
    dedupingInterval: 60000,
  })

  // Transform all pages of data into a single array of suppliers
  const allSuppliers = useMemo(() => {
    if (!data) return []

    return data.flatMap((pageData: any) => {
      if (!pageData?.hits) return []

      return pageData.hits.map((hit: any) => {
        const supplier = hit.document as TypesenseSupplier
        return {
          id: supplier.id,
          'supplier.id': supplier.id,
          'supplier.code': supplier.code,
          'supplier.label': supplier.name,
          'supplier.status': 1,
          'supplier.ctime': '',
          'supplier.address': [],
          'supplier/service': [],
          'supplier/text': [],
          'supplier/media': [],
          productsCount: supplier.productsCount,
          'service.config': supplier['delivery-details'] || {},
          'service.price': supplier['delivery-costs'] || {},
          media: [
            {
              id: '',
              'media.previews': { 1280: supplier.image },
              'media.type': 'default',
              'media.languageid': null,
              'media.mimetype': '',
              'media.domain': 'supplier',
              'media.label': supplier.name,
              'media.preview': supplier.image,
              'media.url': supplier.image,
            },
            ...(supplier.vibeIcon ? [{
              id: '',
              'media.previews': { 1280: supplier.vibeIcon },
              'media.type': 'supplier-image',
              'media.languageid': null,
              'media.mimetype': '',
              'media.domain': 'supplier',
              'media.label': `${supplier.name} vibe icon`,
              'media.preview': supplier.vibeIcon,
              'media.url': supplier.vibeIcon,
            }] : []),
          ],
          text: locales
            .filter((locale) => supplier[`description_${locale}`])
            .map((locale) => ({
              'text.id': '',
              id: '',
              'text.label': 'description',
              'text.type': 'long',
              'text.content': supplier[`description_${locale}`] || '',
              'text.languageid': locale,
              'text.domain': 'supplier',
            })),
          product: [],
        } as SupplierWithProductsCount
      })
    })
  }, [data, locales])

  // Calculate total from the first page
  const total = data?.[0]?.found || 0

  // Check if there are more pages to load
  const hasMore = data && data[data.length - 1]?.hits?.length === limit

  const loadMore = useCallback(() => {
    if (hasMore && !isValidating) {
      setSize(size + 1)
    }
  }, [hasMore, isValidating, setSize, size])

  return {
    data: allSuppliers,
    isLoading: isValidating,
    total,
    error,
    hasMore,
    loadMore,
  }
}

export default useTypesenseSuppliers
